/**
 * Sport to Score Stat Type Mapping
 *
 * Maps sport codes to their primary score stat type used for team_selection questions.
 */

/** Maps sport codes to their score stat type */
export const SPORT_SCORE_STAT_MAP: Record<string, string> = {
  // Football (American)
  nfl: 'points',
  ncaafb: 'points',

  // Basketball
  nba: 'points',
  ncaamb: 'points',
  wnba: 'points',

  // Hockey
  nhl: 'goals',

  // Soccer/Football
  soccer: 'goals',
  mls: 'goals',
  epl: 'goals',
  laliga: 'goals',

  // Baseball
  mlb: 'runs',

  // Golf
  pga: 'strokes',

  // Default fallback
  default: 'points',
};

/**
 * Get the appropriate score stat type for a given sport.
 * @param sportCode - The sport code (e.g. 'nfl', 'nba', 'soccer')
 * @returns The stat type code (e.g. 'points', 'goals', 'runs')
 */
export function getScoreStatTypeForSport(sportCode: string): string {
  const normalizedCode = sportCode.toLowerCase();
  return SPORT_SCORE_STAT_MAP[normalizedCode] || SPORT_SCORE_STAT_MAP.default;
}

/**
 * Sports whose matches can end level in the result that matters for a Moneyline
 * market — i.e. a regulation draw is a real outcome. These get a 3-way Moneyline
 * (Home / Draw / Away); all other sports get a 2-way market (DKPLAT-500, decision D1).
 *
 * Soccer is the canonical case. Sports that resolve ties via overtime / extra
 * innings / shootouts (NBA, MLB, NHL) are NOT included — they cannot finish level.
 */
export const SPORT_CAN_DRAW: Record<string, boolean> = {
  soccer: true,
  mls: true,
  epl: true,
  laliga: true,
};

/** Whether a sport can end in a draw (→ 3-way Moneyline). See SPORT_CAN_DRAW. */
export function sportCanDraw(sportCode: string): boolean {
  return SPORT_CAN_DRAW[sportCode.toLowerCase()] === true;
}

/**
 * Stats where lower values are better (e.g., golf strokes, position).
 * Used by graders to determine the correct winner in player-vs-player questions.
 */
export const LOWER_IS_BETTER_STATS: readonly string[] = [
  // Golf
  'strokes',
  'score',           // golf score (relative to par)
  'position',        // leaderboard position
  'bogeys',          // fewer bogeys = better
  'double_bogeys',   // fewer double bogeys = better
  // MLB pitching + fielding — fewer is better (DKPLAT-480). Without these a
  // "best ERA/WHIP pitcher" question grades the WORST pitcher as the winner.
  'pitching_era',    // earned run average
  'pitching_whip',   // walks + hits per inning pitched
  'pitching_er',     // earned runs allowed
  'pitching_loss',   // losses
  'errors',          // fielding errors (team or player)
];

/**
 * Check if a stat type uses "lower is better" comparison.
 * @param statType - The stat type code to check
 */
export function isLowerBetterStat(statType: string): boolean {
  return LOWER_IS_BETTER_STATS.includes(statType);
}

/** Valid score stat types for team_selection questions */
export const VALID_SCORE_STATS = ['points', 'goals', 'runs', 'strokes'] as const;

/**
 * Check if a stat type is a valid score stat.
 * @param statType - The stat type code to check
 */
export function isScoreStatType(statType: string): boolean {
  return (VALID_SCORE_STATS as readonly string[]).includes(statType);
}
